import { EnvUtil } from './EnvUtil'
import { BrowserUtil } from './BrowserUtil'
import { EventEmitter } from './EventEmitter'

export enum JSBridgeUtilEvent {
  BeforeFunctionCall = 'beforeFunctionCall',
  AfterFunctionCall = 'afterFunctionCall',
}

function setupWebViewJavascriptBridge(callback: any) {
  // 小程序内不加载https://__bridge_loaded__
  if (BrowserUtil.isWeapp() || EnvUtil.isISC() || !BrowserUtil.isUnderApp()) {
    return
  }

  const currentWindow = window as any
  if (currentWindow.WebViewJavascriptBridge) {
    return callback(currentWindow.WebViewJavascriptBridge)
  }
  if (currentWindow.WVJBCallbacks) {
    return currentWindow.WVJBCallbacks.push(callback)
  }
  currentWindow.WVJBCallbacks = [callback]

  const WVJBIframe = document.createElement('iframe')
  WVJBIframe.style.display = 'none'
  WVJBIframe.src = 'https://__bridge_loaded__'
  document.documentElement.appendChild(WVJBIframe)
  setTimeout(() => {
    document.documentElement.removeChild(WVJBIframe)
  }, 0)
}

// ========= android =====
// https://www.jianshu.com/p/ae3a241b3f68
// 参数一：调用java中的方法   submitFromWeb是方法名，必须和Android中注册时候的方法名称保持一致
// 参数二：返回给Android端的数据，可以为字符串，json等信息
// 参数三：js接收到Android传递过来的数据之后的相应处理逻辑

// JS注册事件监听
function androidConnectWebViewJavascriptBridge(callback: any) {
  const currentWindow = window as any
  if (currentWindow.WebViewJavascriptBridge) {
    callback(currentWindow.WebViewJavascriptBridge)
  } else {
    document.addEventListener(
      'WebViewJavascriptBridgeReady',
      function () {
        callback(currentWindow.WebViewJavascriptBridge)
      },
      false
    )
  }
}

// 注册回调函数，第一次连接时调用 初始化函数

class JSBridgeUtil {
  public static eventEmitter = new EventEmitter()

  public static on(type: JSBridgeUtilEvent, handler: Function) {
    this.eventEmitter.on(type, handler)
  }

  public static once(type: JSBridgeUtilEvent, handler: Function) {
    this.eventEmitter.once(type, handler)
  }

  public static fire(type: JSBridgeUtilEvent) {
    this.eventEmitter.fire(type)
  }

  public static remove(type: JSBridgeUtilEvent, handler: Function) {
    this.eventEmitter.remove(type, handler)
  }

  public static _init() {
    // 必须要先初始化
    if (BrowserUtil.isAndroid()) {
      androidConnectWebViewJavascriptBridge((bridge: any) => {
        // 初始化
        bridge.init((message: any, responseCallback: any) => {
          responseCallback(message)
        })
      })
    }
  }

  /**
   * 调用原生方法并且可以有回调
   * @param name 原生方法名
   * @param data 调研参数数据
   * @param callback 回调方法
   */
  public static _callHandler(name: string, data: any, callback: any) {
    if (BrowserUtil.isAndroid()) {
      androidConnectWebViewJavascriptBridge((bridge: any) => {
        console.log('androidConnectWebViewJavascriptBridge callHandler', name, data)
        bridge.callHandler(name, data, callback)
      })
    } else {
      setupWebViewJavascriptBridge(function (bridge: any) {
        bridge.callHandler(name, data, callback)
      })
    }
  }

  public static async asyncCall(methodName: string, data: any = null): Promise<any> {
    // console.log({ event: 'jsbridge_asyncCall_start', methodName })
    this.eventEmitter.fire(JSBridgeUtilEvent.BeforeFunctionCall, methodName, data)

    return new Promise((resolve: any) => {
      // console.log('asyncCall before', methodName, data)
      this._callHandler(methodName, data, (result: any) => {
        // finished = true
        // eslint-disable-next-line no-console
        console.log({
          event: 'jsbridge_asyncCall',
          methodName,
          result,
        })

        resolve(result)

        this.eventEmitter.fire(JSBridgeUtilEvent.AfterFunctionCall, methodName, data)
      })
    })
  }

  /**
   * 注册一个方法用户native调用
   * @param name 注册方法名用于native调用
   * @param callback 回调方法
   */
  public static registerHandler(name: string, callback: any) {
    if (BrowserUtil.isAndroid()) {
      androidConnectWebViewJavascriptBridge((bridge: any) => {
        bridge.registerHandler(name, function (data: any, responseCallback: any) {
          callback(data, responseCallback)
        })
      })
    } else {
      setupWebViewJavascriptBridge(function (bridge: any) {
        bridge.registerHandler(name, function (data: any, responseCallback: any) {
          callback(data, responseCallback)
        })
      })
    }
  }
}

JSBridgeUtil._init()

// After page loaded, disabled android's network error detection.
if (BrowserUtil.isUnderApp() && BrowserUtil.isAndroid()) {
  JSBridgeUtil.asyncCall('onLoadSuccess').finally(() => {
    // noop
  })
}

export { JSBridgeUtil }
